// Google Analytics vendor id: s26
// see https://fielmann.atlassian.net/wiki/x/AYAWyAE
const FIELMANN_GOOGLE_ANALYTICS_VENDOR_ID = 's26'

/**
 * Get the CMP object if available
 * @returns the CMP object
 */
const getCmp = () => {
  if (import.meta.server) {
    return null
  }

  if (
    typeof window !== 'undefined' &&
    '__cmp' in window &&
    typeof window.__cmp === 'function'
  ) {
    // https://help.consentmanager.net/books/cmp/page/javascript-api
    return window.__cmp
  }
}

/**
 * Check if the user has made a choice in the CMP
 * @returns true if the user has made a choice, false if the consent layer is still visible
 */
export const existsCmpUserChoice = () => {
  const cmp = getCmp()
  if (!cmp) {
    return false
  }

  const data = cmp('consentStatus')

  return !!data?.userChoiceExists
}

/**
 * Check if the user has consented to the analytics vendor
 * @returns true if the user has consented to the analytics vendor, false otherwise
 */
export const hasCmpAnalyticsConsent = () => {
  const cmp = getCmp()
  if (!cmp) {
    return false
  }

  const data = cmp('getCMPData')

  return !!data?.vendorConsents?.[FIELMANN_GOOGLE_ANALYTICS_VENDOR_ID]
}

/**
 * Add an event listener to the CMP
 * @param event - The event to listen for
 * @param callback - The callback to call when the event is triggered
 * @param options - The options for the event listener
 */
export const addCmpEventListener = (
  event: string,
  callback: (eventname: string, cmpobject: any, subtype?: string) => void,
  options?: { once?: boolean; signal?: AbortSignal },
) => {
  const cmp = getCmp()
  if (!cmp) {
    return
  }

  // Check if signal is already aborted
  if (options?.signal?.aborted) {
    return
  }

  // Handle once option by wrapping the callback
  let actualCallback = callback
  if (options?.once) {
    actualCallback = (eventname: string, cmpobject: any, subtype?: string) => {
      callback(eventname, cmpobject, subtype)
      // Remove the listener after first call
      cmp('removeEventListener', [event, actualCallback, false], null)
    }
  }

  // Add the event listener
  cmp('addEventListener', [event, actualCallback, false], null)

  // Handle abort signal if provided
  if (options?.signal) {
    // Set up abort listener - must use actualCallback, not callback!
    options.signal.addEventListener(
      'abort',
      () => {
        cmp('removeEventListener', [event, actualCallback, false], null)
      },
      { once: true },
    )
  }
}

/**
 * Add multiple event listeners to the CMP
 * @param events - The events to listen for
 * @param callback - The callback to call when the event is triggered
 * @param options - The options for the event listeners
 */
export const addCmpEventListeners = (
  events: string | string[],
  callback: (eventname: string, cmpobject: any, subtype?: string) => void,
  options?: { once?: boolean; signal?: AbortSignal },
) => {
  const eventArray = Array.isArray(events) ? events : [events]

  eventArray.forEach((event) => {
    addCmpEventListener(event, callback, options)
  })
}
